'use client';

import { Box, Button, Kbd, Separator, Stack, Text, Theme } from '@chakra-ui/react';
import { useEffect, useState, type PropsWithChildren } from 'react';

import mainApi from '@api/mainApi';
import { useAppStore } from '@store/index';
import { useColorMode } from '@ui/color-mode';
import { DialogBackdrop, DialogBody, DialogContent, DialogHeader, DialogRoot, DialogTitle } from '@ui/dialog';
import { Field } from '@ui/field';
import { NumberInputField, NumberInputRoot } from '@ui/number-input';

export default function DevTools({ children }: PropsWithChildren) {
  const [openTools, setOpenTools] = useState(false);
  const [openToolBar, setOpenToolBar] = useState<boolean>(false);

  useEffect(() => {
    const doOpen = sessionStorage?.getItem('devTools-bar') !== 'false';
    setOpenToolBar(doOpen);
  }, []);

  useEffect(() => {
    sessionStorage.setItem('devTools-bar', openToolBar.toString());
  }, [openToolBar]);

  const store = useAppStore();

  // useEffect to listen for a alt+shift+d to open the dev tools
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'KeyD' && event.altKey) {
        setOpenTools((v) => !v);
      }
      if (event.code === 'KeyT' && event.altKey) {
        setOpenToolBar((v) => !v);
      }
      if (event.code === 'KeyR' && event.altKey) {
        store.dispatch(mainApi.util.resetApiState());
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const devToolsApiWaitTime =
    (typeof window !== 'undefined' && sessionStorage?.getItem('devTools-api-wait-time')) || '0';

  const { toggleColorMode } = useColorMode();

  return (
    <>
      {children}
      <Theme appearance="dark">
        <Box
          bgColor="bg"
          px={openToolBar ? '4' : '1'}
          py="1"
          position="fixed"
          bottom={1}
          right={1}
          left={1}
          transform={openToolBar ? 'translateX(0)' : 'translateX(calc(100% - {sizes.16}))'}
          zIndex="docked"
          transitionProperty="transform opacity padding-inline, background-color, border-color"
          transitionDuration="fast"
          transitionTimingFunction="ease-in-out"
          borderRadius="md"
          borderWidth="1px"
          borderColor="border"
          opacity={openToolBar ? 0.9 : 0.4}
          _hover={{
            opacity: 1,
          }}
          cursor={openToolBar ? 'auto' : 'pointer'}
          onClick={() => !openToolBar && setOpenToolBar(true)}
          css={{
            '@media print': {
              display: 'none',
            },
          }}
        >
          <Stack direction="row" alignItems="center">
            <Button
              variant="plain"
              px="0"
              onClick={() => setOpenToolBar((v) => !v)}
              size={openToolBar ? undefined : '2xs'}
              h="7"
            >
              DevToolBar
            </Button>
            <Separator h="4" orientation="vertical" />
            <Button variant="ghost" size="xs" onClick={() => setOpenTools((v) => !v)}>
              <Kbd fontSize="2xs">Alt+D</Kbd> Open DevTools
            </Button>
            <Separator h="4" orientation="vertical" />
            <Button variant="ghost" size="xs" onClick={() => store.dispatch(mainApi.util.resetApiState())}>
              <Kbd fontSize="2xs">Alt+R</Kbd> Reset API
            </Button>
            <Separator h="4" orientation="vertical" />
            <Button variant="ghost" size="xs" onClick={toggleColorMode}>
              Toggle Color Mode
            </Button>
            <Box flex={1} />
            <Button variant="ghost" size="xs" onClick={() => setOpenToolBar((v) => !v)}>
              <Kbd fontSize="2xs">Alt+T</Kbd> Close
            </Button>
          </Stack>
        </Box>
      </Theme>
      <DialogRoot lazyMount open={openTools} onOpenChange={(e) => setOpenTools(e.open)}>
        <DialogBackdrop />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>DevTools</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack>
              <Stack direction="row">
                <Kbd>Alt+D</Kbd> <Text>Open/Close DevTools</Text>
              </Stack>
              <Stack direction="row">
                <Kbd>Alt+T</Kbd> <Text>Open/Close DevToolBar</Text>
              </Stack>
              <Stack direction="row">
                <Kbd>Alt+R</Kbd> <Text>Reset API State (reload RTK Query cache)</Text>
              </Stack>
              <Stack direction="row">
                <Button variant="subtle" size="xs" onClick={toggleColorMode}>
                  Toggle Color Mode
                </Button>
              </Stack>
              <Field label="API Wait Time">
                <NumberInputRoot
                  maxW="200px"
                  defaultValue={devToolsApiWaitTime}
                  onValueChange={(e) => sessionStorage.setItem('devTools-api-wait-time', e.value)}
                >
                  <NumberInputField />
                </NumberInputRoot>
              </Field>
            </Stack>
          </DialogBody>
        </DialogContent>
      </DialogRoot>
    </>
  );
}
